// TopNavLayout.jsx
import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import Icon from "@mui/material/Icon";
import { Badge, Dropdown } from "flowbite-react";
import { IoIosArrowDown, IoMdRefresh } from "react-icons/io";
import { MdOutlinePayments } from "react-icons/md";
import { AnimatePresence, motion } from "framer-motion";
import { useApplication } from "../contexts/ApplicationContext";
import { permissionChecker, removeToken } from "../utils/permissions";
import { calculateExpirationPeriodTest, formatToReal } from "../utils/utils";
import ModalAddCredit from "./modals/ModalAddCredit";
import NotificationBell from "./NotificationBell";
import GlobalChatDock from "../pages/GlobalChatDock";
import { Tooltip } from "react-tooltip";
import "./Sidebar.css";

const SIDEBAR_COLLAPSED_KEY = "relinq-sales-sidebar-collapsed";

/* ===========================
 * Helpers
 * =========================== */
const getInitials = (nameOrEmail = "") => {
  const base = (nameOrEmail || "").trim();
  if (!base) return "?";
  const name = base.includes("@") ? base.split("@")[0] : base;
  const parts = name.split(/[\s._-]+/).filter(Boolean);
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
};

const UserMenuLabel = ({ user }) => {
  const labelText = user?.name || user?.email || "Usuário";
  const initials = getInitials(user?.name || user?.email);

  return (
    <button
      type="button"
      className="group relative flex items-center gap-2 rounded-full border border-[#e45412]/20 bg-white/70 px-3 py-1.5 backdrop-blur-md shadow-sm hover:bg-[#e45412]/90 hover:border-[#e45412]/70 transition-colors"
    >
      <span className="flex h-7 w-7 items-center justify-center rounded-full bg-[#e45412]/10 text-[#e45412] text-xs font-extrabold shadow-sm group-hover:bg-white group-hover:text-[#e45412]">
        {initials}
      </span>
      <span className="max-w-[12rem] truncate text-sm font-semibold text-slate-800 group-hover:text-white">
        {labelText}
      </span>
      <svg
        className="h-4 w-4 text-[#e45412]/70 group-hover:text-white transition-colors"
        viewBox="0 0 20 20"
        fill="currentColor"
        aria-hidden="true"
      >
        <path
          fillRule="evenodd"
          d="M5.23 7.21a.75.75 0 011.06.02L10 11.127l3.71-3.896a.75.75 0 111.08 1.04l-4.24 4.46a.75.75 0 01-1.08 0L5.21 8.27a.75.75 0 01.02-1.06z"
          clipRule="evenodd"
        />
      </svg>
    </button>
  );
};

/* ===========================
 * Componente principal
 * =========================== */
export default function TopNavLayout({ children }) {
  const { user, userPermission, refetchUser } = useApplication();
  const location = useLocation();

  const [isMobileOpen, setIsMobileOpen] = useState(false);
  const [expandedMobile, setExpandedMobile] = useState({});
  const [isOpenModalAddCredit, setIsOpenModalAddCredit] = useState(false);
  const [titlePage, setTitlePage] = useState("Dashboard");
  const [isCollapsed, setIsCollapsed] = useState(() => {
    if (typeof window === "undefined") return false;
    const saved = window.localStorage.getItem(SIDEBAR_COLLAPSED_KEY);
    return saved === "true"; // default = false se não tiver salvo
  }); // sidebar mini/expandida
  const [isChatOpen, setIsChatOpen] = useState(false);
  const [chatUnread, setChatUnread] = useState(0);

  calculateExpirationPeriodTest(user?.Company?.planExpirationDate);
  const logoSrc = "/assets/logo.png";

  const company = user?.Company;
  const companyLabel = company?.fantasyName || company?.name || "Relinq Sales";

  const menuLinks = useMemo(
    () => [
      {
        name: "Visão Geral",
        link: "/dashboard",
        icon: "grading",
        permission: ["USER", "USER_CALENDARS"],
        isCollapsible: false,
        typeCompany: ["ALL"],
      },
      {
        name: "Empresas",
        link: "/companies",
        icon: "apartment",
        permission: ["ADMIN", "POWER_USER"],
        isCollapsible: false,
        typeCompany: ["ALL"],
      },
      {
        name: "Usuários",
        link: "/users",
        icon: "group",
        permission: ["ADMIN"],
        isCollapsible: false,
        typeCompany: ["ALL"],
      },
      {
        name: "Orçamentos",
        link: "/budgets",
        icon: "request_quote",
        permission: ["USER", "ADMIN", "POWER_USER"],
        isCollapsible: false,
        typeCompany: ["ALL"],
      },
    ],
    [setIsChatOpen],
  );

  useEffect(() => {
    if (typeof window === "undefined") return;
    window.localStorage.setItem(
      SIDEBAR_COLLAPSED_KEY,
      isCollapsed ? "true" : "false",
    );
  }, [isCollapsed]);

  useEffect(() => {
    const flat = [];
    menuLinks.forEach((m) => {
      flat.push({ name: m.name, link: m.link });
      (m.children || []).forEach((c) =>
        flat.push({ name: c.name, link: c.link }),
      );
    });
    const found = flat.find((f) => f.link === location.pathname);
    setTitlePage(found?.name || "Dashboard");
  }, [location.pathname, menuLinks]);

  const hasPerm = (item) =>
    permissionChecker(item.permission, userPermission, user, item.typeCompany);

  const topLevel = menuLinks.filter(hasPerm);
  const isActive = (href) => location.pathname === href;

  const toggleMobileGroup = (name) =>
    setExpandedMobile((p) => ({ ...p, [name]: !p[name] }));

  const handleRefresh = () => refetchUser();

  useEffect(() => {
    setIsMobileOpen(false);
  }, [location.pathname]);

  return (
    <>
      <div className="h-screen w-full bg-gradient-to-br from-slate-50 via-slate-100 to-slate-50 flex overflow-hidden">
        {/* ========== SIDEBAR DESKTOP ========== */}
        <motion.aside
          layout
          initial={false}
          animate={{ width: isCollapsed ? 76 : 248 }} // 76px ~ colapsado, 288px ~ aberto
          transition={{ type: "spring", stiffness: 260, damping: 30 }}
          className="hidden lg:flex flex-col border-r border-slate-200/70 bg-gradient-to-b from-white via-slate-50 to-slate-100 backdrop-blur-sm shadow-[4px_0_18px_rgba(15,23,42,0.06)]"
        >
          {/* Logo + toggle */}
          <div className="flex items-center justify-between px-3.5 py-2 border-b border-slate-100/80">
            <Link
              to="/dashboard"
              className="flex items-center gap-3 overflow-hidden"
            >
              <AnimatePresence initial={false}>
                {!isCollapsed ? (
                  <motion.div
                    key="logo-full"
                    initial={{ opacity: 0, x: -8 }}
                    animate={{ opacity: 1, x: 0 }}
                    exit={{ opacity: 0, x: -8 }}
                    transition={{ duration: 0.15 }}
                    className="flex items-center gap-2"
                  >
                    <div className="rounded-2xl px-2 py-1 flex items-center gap-2 shadow-sm">
                      <img
                        src={logoSrc}
                        alt="Logo"
                        className="h-7 w-auto object-contain"
                      />
                      <span className="text-[11px] font-semibold tracking-wide text-gray-600 uppercase">
                        Admin
                      </span>
                    </div>
                  </motion.div>
                ) : null}
              </AnimatePresence>
            </Link>

            <button
              type="button"
              onClick={() => setIsCollapsed((prev) => !prev)}
              className="flex items-center justify-center rounded-full border border-slate-200 bg-white h-12 w-12 min-w-12 text-slate-500 hover:text-[#e45412] hover:border-[#e45412]/40 hover:bg-[#e45412]/5 transition"
              title={isCollapsed ? "Expandir menu" : "Recolher menu"}
            >
              {isCollapsed ? (
                <img className="h-4 w-4" src={"/assets/logo_mobile.png"} />
              ) : null}
              <motion.svg
                className="h-3.5 w-3.5 min-w-3.5"
                viewBox="0 0 24 24"
                fill="none"
                animate={{ rotate: isCollapsed ? 0 : 180 }}
                transition={{ type: "spring", stiffness: 260, damping: 20 }}
              >
                <path
                  d="M10 6L16 12L10 18"
                  stroke="currentColor"
                  strokeWidth="1.8"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </motion.svg>
            </button>
          </div>

          {/* Navegação principal */}
          <nav className="mt-4 flex-1 overflow-y-auto px-2.5 pb-4">
            <div className="space-y-2">
              {topLevel.map((item) => {
                if (item.type === "action") {
                  return (
                    <DesktopNavActionItem
                      key={item.name}
                      icon={item.icon}
                      label={item.name}
                      collapsed={isCollapsed}
                      onClick={item.onClick}
                      hasUnread={item.name === "Mensagens" && chatUnread > 0}
                    />
                  );
                }

                return !item.isCollapsible ? (
                  <DesktopNavItem
                    key={item.name}
                    to={item.link}
                    icon={item.icon}
                    label={item.name}
                    active={isActive(item.link)}
                    collapsed={isCollapsed}
                    highlight={item.highlight}
                  />
                ) : (
                  <DesktopNavGroup
                    key={item.name}
                    label={item.name}
                    icon={item.icon}
                    items={(item.children || []).filter(hasPerm)}
                    isActiveGroup={(item.children || []).some((c) =>
                      isActive(c.link),
                    )}
                    collapsed={isCollapsed}
                  />
                );
              })}
            </div>
          </nav>

          {/* Usuário na base */}
          <div className="border-t border-gray-300 px-3 py-3.5 bg-gray-100">
            <div className="text-center text-sm font-semibold truncate max-w-md">
              {companyLabel}
            </div>
          </div>
        </motion.aside>

        {/* ========== MOBILE SIDEBAR (overlay) ========== */}
        <AnimatePresence>
          {isMobileOpen && (
            <motion.div
              className="lg:hidden fixed inset-0 z-40"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
            >
              <button
                type="button"
                className="absolute inset-0 bg-black/35"
                onClick={() => setIsMobileOpen(false)}
              />

              <motion.aside
                initial={{ x: -260, opacity: 0 }}
                animate={{ x: 0, opacity: 1 }}
                exit={{ x: -260, opacity: 0 }}
                transition={{ duration: 0.18 }}
                className="absolute left-0 top-0 h-full w-72 bg-orange-600 text-white shadow-2xl flex flex-col"
              >
                <div className="px-4 pt-4 pb-3 border-b border-white/10 flex items-center justify-between gap-3 bg-black/10">
                  <div className="flex items-center gap-3">
                    <div className="h-9 w-9 rounded-full bg-white/10 flex items-center justify-center text-sm font-bold">
                      {getInitials(user?.name || user?.email)}
                    </div>
                    <div className="flex flex-col">
                      <span className="text-sm font-semibold truncate max-w-[10rem]">
                        {user?.name || "Usuário"}
                      </span>
                      <span className="text-xs text-white/70 truncate max-w-[12rem]">
                        {user?.email}
                      </span>
                    </div>
                  </div>

                  <button
                    type="button"
                    className="inline-flex items-center justify-center rounded-full bg-white/10 hover:bg-white/20 p-1.5"
                    onClick={() => setIsMobileOpen(false)}
                  >
                    <svg
                      className="h-5 w-5"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="1.8"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    >
                      <path d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </div>

                <div className="flex-1 overflow-y-auto px-3 py-3">
                  {topLevel.map((item) =>
                    item.type === "action" ? (
                      <MobileActionItem
                        key={item.name}
                        icon={item.icon}
                        label={item.name}
                        onClick={() => {
                          item.onClick?.();
                          setIsMobileOpen(false);
                        }}
                        hasUnread={item.name === "Mensagens" && chatUnread > 0}
                      />
                    ) : !item.isCollapsible ? (
                      <MobileItem
                        key={item.name}
                        to={item.link}
                        icon={item.icon}
                        active={isActive(item.link)}
                        label={item.name}
                      />
                    ) : (
                      <MobileGroup
                        key={item.name}
                        label={item.name}
                        icon={item.icon}
                        open={!!expandedMobile[item.name]}
                        onToggle={() => toggleMobileGroup(item.name)}
                        childrenItems={(item.children || []).filter(hasPerm)}
                        isActiveGroup={(item.children || []).some((c) =>
                          isActive(c.link),
                        )}
                      />
                    ),
                  )}
                </div>

                <div className="border-t border-white/10 px-4 py-3 space-y-1 bg-black/15">
                  <Link
                    to="/profile"
                    className="block text-sm py-1 text-white/90 hover:text-white hover:underline"
                    onClick={() => setIsMobileOpen(false)}
                  >
                    Meu perfil
                  </Link>
                  <Link
                    to="/assinatura"
                    className="block text-sm py-1 text-white/90 hover:text-white hover:underline"
                    onClick={() => setIsMobileOpen(false)}
                  >
                    Gerenciar assinatura
                  </Link>
                  <a
                    target="_blank"
                    href="https://wa.me/+5515996730404"
                    rel="noreferrer"
                    className="block text-sm py-1 text-white/90 hover:text-white hover:underline"
                    onClick={() => setIsMobileOpen(false)}
                  >
                    Entre em contato
                  </a>
                  <button
                    type="button"
                    className="mt-1 w-full text-left text-sm font-semibold text-red-100 hover:text-white"
                    onClick={removeToken}
                  >
                    Sair
                  </button>
                </div>
              </motion.aside>
            </motion.div>
          )}
        </AnimatePresence>

        {/* ========== CONTEÚDO PRINCIPAL ========== */}
        <div className="flex-1 flex flex-col min-w-0">
          {/* Header topo */}
          <header className="sticky top-0 z-30 bg-white/85 backdrop-blur-md border-b border-slate-100 shadow-[0_8px_20px_rgba(15,23,42,0.04)]">
            <div className="flex items-center justify-between px-3 md:px-6 py-2.5">
              {/* Esquerda: botão mobile + título */}
              <div className="flex items-center gap-3">
                {/* burger mobile */}
                <button
                  className="lg:hidden inline-flex items-center justify-center rounded-md p-2 text-gray-700 hover:text-[#e45412] hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-[#e45412]/40"
                  onClick={() => setIsMobileOpen((o) => !o)}
                  aria-label="Abrir menu"
                >
                  <svg className="h-6 w-6" viewBox="0 0 24 24" fill="none">
                    <path
                      d="M4 6h16M4 12h16M4 18h16"
                      stroke="currentColor"
                      strokeWidth="1.8"
                      strokeLinecap="round"
                    />
                  </svg>
                </button>

                <div className="flex flex-col">
                  <div className="flex items-center gap-2">
                    <h1 className="text-sm md:text-base font-semibold text-slate-900">
                      {titlePage}
                    </h1>
                  </div>
                  <p className="text-[11px] text-slate-400 hidden sm:block">
                    Bem-vindo(a) de volta,{" "}
                    <span className="font-medium text-slate-500">
                      {user?.name || "usuário"}
                    </span>
                    .
                  </p>
                </div>
              </div>

              {/* Direita: status conta + notificações + user menu */}
              <div className="flex items-center gap-2 md:gap-3">
                <div className="hidden sm:block">
                  <Dropdown
                    label={<UserMenuLabel user={user} />}
                    arrowIcon={false}
                    inline
                  >
                    <Dropdown.Header>
                      <span className="block text-sm font-medium truncate">
                        {user?.email}
                      </span>
                    </Dropdown.Header>

                    <Dropdown.Item onClick={removeToken}>Sair</Dropdown.Item>
                  </Dropdown>
                </div>
              </div>
            </div>
          </header>

          {/* Main content area */}
          <main className="flex-1 overflow-y-auto px-3 md:px-6 pt-3 pb-64 md:py-4 overflow-x-hidden">
            {children}
          </main>
        </div>
      </div>

      {/* Dock de chat global, alinhado com a sidebar */}
      <GlobalChatDock
        sidebarCollapsed={isCollapsed}
        open={isChatOpen}
        onClose={() => setIsChatOpen(false)}
        onUnreadChange={setChatUnread}
      />

      <Tooltip
        id="sidebar-tooltip"
        place="right"
        offset={10}
        style={{
          backgroundColor: "#0f172a",
          color: "#f9fafb",
          fontSize: "11px",
          padding: "6px 8px",
          borderRadius: "999px",
        }}
      />
    </>
  );
}

/* ===========================
 * Subcomponentes de navegação
 * =========================== */

function DesktopNavItem({ to, icon, label, active, collapsed, highlight }) {
  const tooltipProps = collapsed
    ? {
        "data-tooltip-id": "sidebar-tooltip",
        "data-tooltip-content": label,
      }
    : {};

  const baseClasses = active
    ? "bg-gradient-to-r from-[#e45412]/14 via-[#e45412]/5 to-transparent text-[#e45412] border border-[#e45412]/30 shadow-sm"
    : highlight
      ? "text-[#f97316]"
      : "text-slate-600 hover:bg-slate-50 hover:text-[#e45412] border border-transparent";

  return (
    <Link
      to={to}
      {...tooltipProps}
      className={`group flex items-center gap-1 rounded-2xl px-2 py-1 text-sm transition-all mb-0.5
        ${baseClasses} ${collapsed ? "justify-center px-1" : ""}`}
    >
      {icon && (
        <div
          className={`flex h-8 w-8 items-center justify-center rounded-xl text-[22px] transition-colors ${
            highlight ? "highlight-icon" : ""
          }`}
        >
          <Icon fontSize="inherit">{icon}</Icon>
        </div>
      )}
      {!collapsed && <span className="font-medium truncate">{label}</span>}
    </Link>
  );
}

function DesktopNavActionItem({ icon, label, collapsed, onClick, hasUnread }) {
  const tooltipProps = collapsed
    ? {
        "data-tooltip-id": "sidebar-tooltip",
        "data-tooltip-content": label,
      }
    : {};

  return (
    <button
      type="button"
      onClick={onClick}
      {...tooltipProps}
      className={`group flex items-center gap-1 rounded-2xl px-2 py-1 text-sm mb-0.5 transition-all
      text-slate-600 hover:text-[#e45412] hover:bg-slate-50 border border-transparent w-full ${
        collapsed ? "justify-center px-1" : ""
      }`}
    >
      {icon && (
        <div className="relative flex h-8 w-8 items-center justify-center rounded-xl border-slate-200 text-[22px] text-slate-500 group-hover:border-[#e45412]/40 group-hover:bg-[#e45412]/5 group-hover:text-[#e45412] transition-colors">
          <Icon fontSize="inherit">{icon}</Icon>
          {hasUnread && (
            <span className="absolute -top-0.5 -right-0.5 h-2.5 w-2.5 rounded-full bg-red-500 ring-2 ring-white" />
          )}
        </div>
      )}
      {!collapsed && <span className="font-medium truncate pr-1">{label}</span>}
    </button>
  );
}

function DesktopNavGroup({ label, icon, items, isActiveGroup, collapsed }) {
  const [open, setOpen] = useState(isActiveGroup);
  const ref = useRef(null);

  useEffect(() => {
    const onClick = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("click", onClick);
    return () => document.removeEventListener("click", onClick);
  }, []);

  if (collapsed) {
    return (
      <div className="relative" ref={ref}>
        <button
          type="button"
          onClick={() => setOpen((o) => !o)}
          className={`w-full flex items-center justify-center rounded-2xl px-1 py-2.5 text-sm mb-0.5 transition
            ${
              isActiveGroup
                ? "bg-[#e45412]/10 text-[#e45412] border border-[#e45412]/30"
                : "text-slate-600 hover:text-[#e45412] hover:bg-slate-50 border border-transparent"
            }`}
          title={label}
        >
          {icon && (
            <div
              className={`flex h-9 w-9 items-center justify-center rounded-xl border text-[19px]
              ${
                isActiveGroup
                  ? "border-[#e45412]/60 bg-[#e45412]/10 text-[#e45412]"
                  : "border-slate-200 bg-white text-slate-500"
              }`}
            >
              <Icon fontSize="inherit">{icon}</Icon>
            </div>
          )}
        </button>

        <AnimatePresence>
          {open && (
            <motion.div
              initial={{ opacity: 0, x: 4 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: 4 }}
              transition={{ duration: 0.12 }}
              className="absolute left-full top-0 ml-2 w-56 rounded-2xl overflow-hidden border border-gray-200 bg-white shadow-xl z-20"
            >
              <div className="px-3 py-2 border-b border-slate-100">
                <p className="text-xs font-semibold text-slate-600">{label}</p>
              </div>
              <div className="py-1">
                {items.map((child) => (
                  <Link
                    key={child.link}
                    to={child.link}
                    className="flex items-center gap-2 px-3 py-2 text-sm text-gray-700 hover:text-[#e45412] hover:bg-[#e45412]/5"
                  >
                    {child.icon && (
                      <Icon className="text-[18px] text-gray-500">
                        {child.icon}
                      </Icon>
                    )}
                    <span className="truncate">{child.name}</span>
                  </Link>
                ))}
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    );
  }

  return (
    <div className="mb-0.5" ref={ref}>
      <button
        onClick={() => setOpen((o) => !o)}
        className={`w-full flex items-center justify-between px-2.5 py-2.5 rounded-2xl text-sm transition-all border
          ${
            isActiveGroup
              ? "bg-gradient-to-r from-[#e45412]/14 via-[#e45412]/5 to-transparent text-[#e45412] border-[#e45412]/30 shadow-sm"
              : "text-slate-600 hover:text-[#e45412] hover:bg-slate-50 border-transparent"
          }`}
      >
        <span className="flex items-center gap-3">
          {icon && (
            <div
              className={`flex h-9 w-9 items-center justify-center rounded-xl border text-[19px]
              ${
                isActiveGroup
                  ? "border-[#e45412]/60 bg-[#e45412]/10 text-[#e45412]"
                  : "border-slate-200 bg-white text-slate-500"
              }`}
            >
              <Icon fontSize="inherit">{icon}</Icon>
            </div>
          )}
          <span className="font-medium">{label}</span>
        </span>
        <IoIosArrowDown
          className={`text-current transition-transform ${
            open ? "rotate-180" : ""
          }`}
        />
      </button>

      <AnimatePresence initial={false}>
        {open && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.18 }}
            className="overflow-hidden pl-10 mt-1 space-y-0.5"
          >
            {items.map((child) => (
              <Link
                key={child.link}
                to={child.link}
                className="flex items-center gap-2 px-2 py-1.5 rounded-xl text-sm text-slate-600 hover:text-[#e45412] hover:bg-slate-50"
              >
                {child.icon && (
                  <Icon className="text-[16px] text-slate-400">
                    {child.icon}
                  </Icon>
                )}
                <span>{child.name}</span>
              </Link>
            ))}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

/* ===========================
 * Componentes mobile
 * =========================== */

function MobileItem({ to, icon, label, active }) {
  return (
    <Link
      to={to}
      className={`flex items-center gap-2 px-3 py-2 rounded-lg text-sm mb-1 transition
        ${
          active
            ? "bg-white/15 text-white border border-white/20"
            : "text-white/85 hover:text-white hover:bg-white/10"
        }`}
    >
      {icon && <Icon className="text-[18px] text-white/85">{icon}</Icon>}
      <span className="font-medium">{label}</span>
    </Link>
  );
}

function MobileActionItem({ icon, label, onClick, hasUnread }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="w-full flex items-center gap-2 px-3 py-2 rounded-lg text-sm mb-1 transition text-white/85 hover:text-white hover:bg-white/10 text-left"
    >
      {icon && (
        <div className="relative flex items-center justify-center">
          <Icon className="text-[18px] text-white/85">{icon}</Icon>
          {hasUnread && (
            <span className="absolute -top-0.5 -right-0.5 h-2.5 w-2.5 rounded-full bg-red-500 ring-2 ring-[#7A082D]" />
          )}
        </div>
      )}
      <span className="font-medium">{label}</span>
    </button>
  );
}

function MobileGroup({
  label,
  icon,
  open,
  onToggle,
  childrenItems,
  isActiveGroup,
}) {
  return (
    <div className="mb-1">
      <button
        onClick={onToggle}
        className={`w-full flex items-center justify-between px-3 py-2 rounded-lg text-sm transition
          ${
            isActiveGroup
              ? "bg-white/15 text-white border border-white/20"
              : "text-white/85 hover:text-white hover:bg-white/10"
          }`}
      >
        <span className="flex items-center gap-2">
          {icon && <Icon className="text-[18px] text-white/85">{icon}</Icon>}
          <span className="font-medium">{label}</span>
        </span>
        <IoIosArrowDown
          className={`text-white/80 transition-transform ${
            open ? "rotate-180" : ""
          }`}
        />
      </button>

      <AnimatePresence initial={false}>
        {open && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="overflow-hidden pl-2 mt-1"
          >
            {(childrenItems || []).map((child) => (
              <Link
                key={child.link}
                to={child.link}
                className="flex items-center gap-2 px-3 py-2 rounded-md text-sm text-white/85 hover:text-white hover:bg-white/10 mb-1"
              >
                {child.icon && (
                  <Icon className="text-[16px] text-white/80">
                    {child.icon}
                  </Icon>
                )}
                <span>{child.name}</span>
              </Link>
            ))}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
